<script>
const CREATOR_OPTIONS = {
    byProduct: "by-product",
    byPrompt: "by-prompt",
};
</script>

<script setup>
import cCreatorProductList from "./cCreatorProductList.vue";
import cCreatorProductForm from "./cCreatorProductForm.vue";
import { FormSwitch } from "@easy/ui/forms";
import { t } from "@/i18n";
import { computed, ref, watch } from "vue";
import { useAsyncState, watchDebounced } from "@vueuse/core";
import { userProductsService } from "@/shared/user/user-products.service";
import { usePagination } from "@/shared/utils/usePagination";
import { useGetters } from "@/store/vuex-composition-mappers";
import { userService } from "@/shared/user/user.service";

const props = defineProps({
    error: { type: String, default: "" },
    hidePrompt: { type: Boolean, default: false },
    showProductPickerOnly: { type: Boolean, default: false },
    productsForTimer: { type: Boolean, default: false },
    promptPlaceholder: { type: [String, undefined], default: undefined },
});
const emit = defineEmits(["productInfoUpdated"]);
const productSelectLater = defineModel("productSelectLater", { type: Boolean, required: true });

const options = computed(() => [
    { value: CREATOR_OPTIONS.byProduct, label: t("shared.creator.with_product") },
    { value: CREATOR_OPTIONS.byPrompt, label: t("shared.creator.no_product") },
]);
const productSearch = ref("");
const chosenOption = ref(CREATOR_OPTIONS.byProduct);
const projectPrompt = ref("");
const selectedProduct = ref(null);

const { newProductLink } = useGetters(["newProductLink"]);

const { updatePaginationUnwrapResult, page, pageSize, totalPages } = usePagination({
    size: 3,
    fetchAction: () => loadProducts(),
});

const {
    state: products,
    isLoading: areProductsLoading,
    execute: loadProducts,
} = useAsyncState(
    () =>
        userProductsService
            .getAll({
                search: productSearch.value,
                page: page.value,
                size: pageSize.value,
                hasActiveUntil: props.productsForTimer ? true : undefined,
            })
            .then(updatePaginationUnwrapResult),
    [],
    { immediate: true }
);

watch([selectedProduct, projectPrompt, chosenOption], () => {
    emit("productInfoUpdated", {
        mode: chosenOption,
        productDetails: selectedProduct,
        promptDescription: projectPrompt,
    });
});

watchDebounced(
    productSearch,
    () => {
        loadProducts();
    },
    { debounce: 500 }
);

async function handleNewProductCreation() {
    const { magicUrl } = await userService.forgeMagicLink(newProductLink.value);
    window.open(magicUrl, "_blank");
}
</script>

<template>
    <div class="space-y-6">
        <FormSwitch v-if="!showProductPickerOnly" :options="options" v-model="chosenOption" />
        <cCreatorProductList
            v-if="chosenOption === CREATOR_OPTIONS.byProduct"
            v-model:currentPage="page"
            v-model:query="productSearch"
            v-model:selectedProduct="selectedProduct"
            v-model:selectLater="productSelectLater"
            :items="products"
            :totalPages="totalPages"
            :isLoading="areProductsLoading"
            @addNewProduct="handleNewProductCreation()"
        />
        <template v-if="!showProductPickerOnly && chosenOption === CREATOR_OPTIONS.byPrompt">
            <slot name="templated"></slot>
            <cCreatorProductForm
                v-if="!hidePrompt"
                v-model="projectPrompt"
                :placeholder="promptPlaceholder"
            />
        </template>
        <div class="p-3 mt-2 text-sm ct-text-error ct-word-breaks" v-if="error">
            {{ error }}
        </div>
    </div>
</template>
